import type { TemplateField, TemplateContentFields, EditorStep, EditorCardStep, EditorCardGroupStep } from "@/types/activity";

export interface ValidationError {
  fieldName: string;
  fieldLabel: string;
  message: string;
}

/**
 * Validate a card's content against its template fields.
 * Returns an array of validation errors (empty = valid).
 */
export function validateCardContent(
  content: Record<string, unknown>,
  fields: TemplateField[],
): ValidationError[] {
  const errors: ValidationError[] = [];

  for (const field of fields) {
    const value = content[field.name];
    const label = field.label || field.name;

    if (field.required) {
      if (isEmptyValue(value, field.type)) {
        errors.push({
          fieldName: field.name,
          fieldLabel: label,
          message: `Le champ « ${label} » est obligatoire`,
        });
        continue;
      }
    }

    // Type-specific validations (only if value is present)
    if (value !== undefined && value !== null && value !== "") {
      if (field.type === "integer" && typeof value === "number") {
        if (field.minimum !== undefined && value < field.minimum) {
          errors.push({ fieldName: field.name, fieldLabel: label, message: `Minimum : ${field.minimum}` });
        }
        if (field.maximum !== undefined && value > field.maximum) {
          errors.push({ fieldName: field.name, fieldLabel: label, message: `Maximum : ${field.maximum}` });
        }
      }

      if (field.type === "string" && field.format === "uri" && typeof value === "string") {
        try {
          new URL(value);
        } catch {
          errors.push({ fieldName: field.name, fieldLabel: label, message: `URL invalide` });
        }
      }

      if (field.type === "array" && field.minItems !== undefined && Array.isArray(value)) {
        if (value.length < field.minItems) {
          errors.push({ fieldName: field.name, fieldLabel: label, message: `Minimum ${field.minItems} élément(s)` });
        }
      }

      if (field.type === "select" && field.options?.length && typeof value === "string") {
        const keys = field.options.map((opt) => (typeof opt === "string" ? opt : opt.key));
        if (!keys.includes(value)) {
          errors.push({ fieldName: field.name, fieldLabel: label, message: `Valeur invalide. Options : ${keys.join(", ")}` });
        }
      }
    }
  }

  return errors;
}

function isEmptyValue(value: unknown, type: string): boolean {
  if (value === undefined || value === null) return true;
  if (type === "string" || type === "file" || type === "select" || type === "video") return typeof value === "string" && value.trim() === "";
  if (type === "audio") {
    const audio = value as { url?: string };
    return !audio.url || audio.url.trim() === "";
  }
  if (type === "transcript") return !Array.isArray(value) || value.length === 0;
  if (type === "array") return !Array.isArray(value) || value.length === 0;
  if (type === "boolean") return false; // boolean is never "empty"
  if (type === "score_points") return !value || typeof value !== "object" || Object.keys(value as object).length === 0;
  return false;
}

/**
 * Validate all steps and return a set of localIds that have errors.
 */
export function validateAllSteps(
  steps: EditorStep[],
  getTemplateFields: (templateId: string) => TemplateContentFields | null,
): Map<string, ValidationError[]> {
  const errorMap = new Map<string, ValidationError[]>();

  for (const step of steps) {
    if (step.type === "card") {
      const tf = getTemplateFields(step.templateId);
      if (tf?.fields) {
        const errors = validateCardContent(step.content, tf.fields);
        if (errors.length > 0) {
          errorMap.set(step.localId, errors);
        }
      }
    } else {
      const group = step as EditorCardGroupStep;
      const groupErrors: ValidationError[] = [];
      for (const card of group.cards) {
        const tf = getTemplateFields(card.templateId);
        if (tf?.fields) {
          const cardErrors = validateCardContent(card.content, tf.fields);
          groupErrors.push(...cardErrors);
        }
      }
      if (groupErrors.length > 0) {
        errorMap.set(step.localId, groupErrors);
      }
    }
  }

  return errorMap;
}
